import { t } from 'i18next';
import { debounce, isNil, round } from 'lodash';
import type { SliderProps } from 'rc-slider';
import RcSlider from 'rc-slider';
import { useCallback, useId, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';
import { Input } from '../input';
import InputLabelWithHint from '../input-label/input-label-with-hint';
import Typography from '../typography/typography';

const getStepDecimalPlaces = (step: number): number => {
  if (!Number.isFinite(step) || step <= 0) {
    return 0;
  }

  const [, fraction = ''] = step.toString().split('.');
  return fraction.length;
};

const snapToStep = (value: number, step: number): number => {
  const decimalPlaces = getStepDecimalPlaces(step);
  const valueRounded = round(value, decimalPlaces);
  const adjustedValue = round(valueRounded / step) * step;
  return round(adjustedValue, decimalPlaces);
};

export interface ISlider extends SliderProps {
  label: string;
  hideValue?: boolean;
  isPercentage?: boolean;
  inputId?: string;
  showLabel?: boolean;
  hasError?: boolean;
  step?: number;
  hintText?: string;
  /**
   * Number of decimal places to display in the numeric input. This only
   * formats the displayed value and does not affect slider rounding or events.
   */
  decimalPlaces?: number;
}

export const Slider = ({
  label,
  hideValue = false,
  disabled,
  value,
  min = 0,
  max = 100,
  isPercentage = false,
  defaultValue,
  inputId,
  hasError,
  showLabel = true,
  step = 1,
  hintText,
  decimalPlaces,
  onChange = () => undefined,
  ...rest
}: ISlider) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState(value);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const id = useId();

  const handleOnChange = useCallback(
    (nextValue: number) => {
      const finalValue = snapToStep(nextValue, step);

      if (finalValue >= min && finalValue <= max) {
        setShowErrorMessage(false);
        onChange(finalValue);
      } else {
        setShowErrorMessage(true);
        onChange(finalValue < min ? min : max);
      }
    },
    [onChange, min, max, step]
  );

  const hideHandle = disabled && value === min;
  const resolvedDecimalPlaces = decimalPlaces ?? getStepDecimalPlaces(step);
  const inputDefaultValue =
    disabled && !value
      ? undefined
      : value?.toString() || defaultValue?.toString() || min.toString();
  const displayedInputValue =
    resolvedDecimalPlaces === 0 ||
    isNil(inputDefaultValue) ||
    !Number.isFinite(Number(inputDefaultValue))
      ? inputDefaultValue
      : Number(inputDefaultValue).toFixed(resolvedDecimalPlaces);

  return (
    <Wrapper>
      <InputLabelWithHint
        hasError={hasError}
        inputId={inputId || id}
        name={label}
        showLabel={showLabel}
        hintText={hintText}
      />
      <Container>
        {/* These "bumpers" make it that both the rail and the thumb align with the input label */}
        <SliderContainer>
          <RailLeftBumper
            onClick={() => onChange(min)}
            $disabled={disabled}
            $hideHandle={hideHandle}
          />
          <RcSlider
            railStyle={{
              backgroundColor: theme.palette.neutral[60],
              height: '4px',
              borderRadius: '0px',
            }}
            trackStyle={{
              background: disabled
                ? theme.palette.neutral[70]
                : theme.palette.primary.main,
              zIndex: 3,
              height: '4px',
              borderRadius: '0px',
            }}
            handleStyle={{
              height: '16px',
              width: '16px',
              background: theme.palette.neutral['00'],
              border: disabled
                ? `2px solid ${theme.palette.neutral[70]}`
                : `2px solid ${theme.palette.primary.main}`,
              marginTop: '-6px',
              marginLeft: '0px',
              zIndex: 4,
              display: hideHandle ? 'none' : 'block',
            }}
            ariaLabelForHandle={`slider handle for ${label}`}
            step={step}
            disabled={disabled}
            value={Number(value)}
            min={min}
            max={max}
            defaultValue={defaultValue || min}
            {...rest}
            onChange={handleOnChange}
          />

          <RailRightBumper onClick={() => onChange(max)} />
        </SliderContainer>
        {!hideValue && (
          <InputContainer>
            <StyledInput
              id={inputId || id}
              key={`${inputValue}${value}`}
              name={'value'}
              showLabel={false}
              type="number"
              min={min}
              max={max}
              defaultValue={displayedInputValue}
              width="68px"
              height="38px"
              onChange={debounce((event) => {
                handleOnChange(Number(event.target.value));
                setInputValue(event.target.value);
              }, 1000)}
              disabled={disabled}
            />
            {isPercentage && (
              <Typography variant={TypographyVariant.bodyMd1}>%</Typography>
            )}
          </InputContainer>
        )}
      </Container>
      <TextContainer>
        {showErrorMessage && (
          <ErrorTypography
            variant={TypographyVariant.bodySm2}
            color={theme.palette.neutral[90]}
          >
            {t('errors.invalidValueError')}
          </ErrorTypography>
        )}
      </TextContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    gap: ${theme.spacing(2)};
    flex-direction: row;
    align-items: center;
    & .rc-slider-disabled {
      background-color: transparent;
    }
    .rc-slider-handle {
      &:focus {
        outline: 2px solid ${theme.palette.focus};
        outline-offset: -1px;
      }
    }
  `}
`;

const SliderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const getTrackColor = (isHandleHidden: boolean | undefined, theme: ITheme) => {
  return isHandleHidden ? theme.palette.neutral[60] : theme.palette.neutral[70];
};

const RailLeftBumper = styled.div`
  ${({
    theme,
    $disabled,
    $hideHandle,
  }: {
    theme: ITheme;
    $disabled?: boolean;
    $hideHandle?: boolean;
  }) => css`
    width: ${theme.spacing(1)};
    background-color: ${$disabled
      ? getTrackColor($hideHandle, theme)
      : theme.palette.primary.main};
    border-radius: 100px 0px 0px 100px;
    height: 4px;
  `}
`;

const RailRightBumper = styled.div`
  ${({ theme }) => css`
    width: ${theme.spacing(1)};
    background-color: ${theme.palette.neutral[60]};
    border-radius: 0px 100px 100px 0px;
    height: 4px;
  `}
`;

const StyledInput = styled(Input)`
  ${({ theme }) => css`
    ${theme.typography.bodyMd1};
  `}
`;

const InputContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    margin-right: ${theme.spacing(1)};
  `}
`;

const TextContainer = styled.div`
  ${({ theme }) => css`
    min-height: 16px;
    margin: ${theme.spacing(1, 0)};
  `}
`;

const ErrorTypography = styled(Typography)`
  ${({ theme }) => css`
    margin-left: ${theme.spacing(1)};
  `}
`;

export default Slider;
